<template>
  <div class="step-calculation">
    <div class="step-header">
      <h2>{{ t('Quanto e como calcular?') }}</h2>
      <p class="step-description">
        {{ t('Defina o valor ou percentual e a estrutura de cálculo.') }}
      </p>
    </div>

    <!-- Helper for Commission Rules -->
    <div v-if="isCommissionRule" class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Entendendo as estruturas de comissão:') }}</strong>
        <ul class="commission-types">
          <li>
            <strong>{{ t('Simples:') }}</strong>
            {{ t('Única taxa sobre toda a receita. Ex: 10% de R$50.000 = R$5.000') }}
          </li>
          <li>
            <strong>{{ t('Escalonada:') }}</strong>
            {{ t('Quando a receita atinge um limite, taxa MUDA e se aplica a TODA a receita') }}
          </li>
          <li>
            <strong>{{ t('Progressiva:') }}</strong>
            {{ t('Como IR: diferentes taxas para diferentes faixas (soma das partes)') }}
          </li>
        </ul>
        <Button
          data-testid="payroll-payment-rules-step4-comparison-button"
          :label="t('Ver Comparação Visual')"
          icon="pi pi-eye"
          size="small"
          outlined
          @click="showComparison = true"
        />
      </div>
    </div>

    <form @submit.prevent="handleNext" class="wizard-form">
      <!-- Commission Structure Selection (Commission Rules Only) -->
      <div v-if="isCommissionRule" class="form-field">
        <label>{{ t('Estrutura de Comissão') }} *</label>
        <div class="structure-options">
          <button
            v-for="structure in commissionStructures"
            :key="structure.id"
            type="button"
            :data-testid="`payroll-payment-rules-step4-structure-${structure.id}-option`"
            class="structure-option"
            :class="{ 'selected': wizardData.commission_structure === structure.id }"
            @click="selectCommissionStructure(structure.id)"
          >
            <div class="card-icon">
              <i :class="structure.icon"></i>
            </div>
            <div class="card-content">
              <h3>{{ structure.name }}</h3>
              <p class="card-description">{{ structure.description }}</p>
            </div>
            <div class="card-indicator"></div>
          </button>
        </div>
      </div>

      <!-- Period Type (Fixed Rules Only) -->
      <div v-if="wizardData.rule_type === 'fixed'" class="form-field">
        <label>
          {{ t('Tipo de Período') }} *
          <i
            class="pi pi-question-circle tooltip-icon"
            v-tooltip="t('Usado para cálculo proporcional do salário fixo quando o mês tem dias variados')"
          ></i>
        </label>
        <select-list
          elem-id="payroll_period_type"
          data-testid="payroll-payment-rules-step4-period-select"
          v-model="wizardData.period_type"
          :itens-select="periodTypes"
          :placeholder="t('Selecione o tipo de período')"
          :class="{ 'p-invalid': !isPeriodTypeValid }"
        />
        <small class="field-help">
          {{ t('Exemplo: Mensal (30 dias) = R$2.000 ÷ 30 = R$66,67 por dia') }}
        </small>
        <small v-if="!isPeriodTypeValid" class="p-error">
          {{ t('Tipo de período é obrigatório para salário fixo') }}
        </small>
      </div>

      <!-- Amount Input (Simple Commission or Fixed) -->
      <div v-if="shouldShowAmountInput" class="form-field">
        <label>
          {{ isCommissionRule ? t('Percentual de Comissão') : t('Valor Fixo') }} *
          <i
            class="pi pi-question-circle tooltip-icon"
            v-tooltip="isCommissionRule ? t('Percentual que será aplicado sobre a receita') : t('Valor mensal que o funcionário receberá independente dos serviços')"
          ></i>
        </label>

        <!-- Fixed Amount Input -->
        <input-currency
          v-if="!isCommissionRule"
          elem-id="payroll_amount"
          data-testid="payroll-payment-rules-step4-amount-currency"
          v-model="wizardData.amount"
          :placeholder="t('R$ 0,00')"
          :class="{ 'p-invalid': !isAmountValid }"
        />

        <!-- Commission Percentage Slider -->
        <div v-else class="commission-slider-container">
          <div class="commission-value-display">
            {{ n(wizardData.amount || 0, 'percent') }}
          </div>
          <input-slider
            data-testid="payroll-payment-rules-step4-commission-slider"
            v-model="commissionPercentage"
            :min="0"
            :max="100"
            :step="0.25"
            :class="{ 'p-invalid': !isAmountValid }"
          />
          <div class="slider-labels">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
          <small v-if="!isAmountValid" class="p-error">
            {{ t('Percentual deve ser maior que 0% e menor ou igual a 100%') }}
          </small>
          <div v-if="commissionPercentage > 20" class="rate-warning">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ t('Taxa alta! Típico é 5-15%. Tem certeza?') }}</span>
          </div>
        </div>
      </div>

      <!-- Bracket Editor (Tiered/Progressive Commission) -->
      <div v-if="shouldShowBracketEditor" class="form-field bracket-field">
        <label>{{ t('Faixas de Comissão') }} *</label>
        <div class="bracket-editor">
          <div v-for="(bracket, index) in brackets" :key="index" class="bracket-row">
            <div class="bracket-fields">
              <div class="field-group">
                <label>{{ t('De') }}</label>
                <input-currency
                  :elem-id="`payroll_bracket_min_${index}`"
                  :data-testid="`payroll-payment-rules-step4-bracket-min-${index}`"
                  v-model="bracket.min"
                  :placeholder="t('R$ 0,00')"
                  :disabled="index > 0"
                />
              </div>
              <div class="field-group">
                <label>{{ t('Até') }}</label>
                <input-currency
                  :elem-id="`payroll_bracket_max_${index}`"
                  :data-testid="`payroll-payment-rules-step4-bracket-max-${index}`"
                  v-model="bracket.max"
                  :placeholder="index === brackets.length - 1 ? t('R$ ∞') : t('R$ 0,00')"
                  :disabled="index === brackets.length - 1"
                />
              </div>
              <div class="field-group">
                <label>{{ t('Taxa') }}</label>
                <input-number
                  :elem-id="`payroll_bracket_rate_${index}`"
                  :data-testid="`payroll-payment-rules-step4-bracket-rate-${index}`"
                  v-model="bracket.ratePercent"
                  suffix="%"
                  :min="0"
                  :max="100"
                  :step="0.25"
                />
              </div>
              <div class="field-group-actions">
                <Button
                  v-if="brackets.length > 1 && index < brackets.length - 1"
                  :data-testid="`payroll-payment-rules-step4-bracket-remove-${index}`"
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  @click="removeBracket(index)"
                  type="button"
                />
              </div>
            </div>
          </div>
          <Button
            data-testid="payroll-payment-rules-step4-bracket-add-button"
            :label="t('Adicionar Faixa')"
            icon="pi pi-plus"
            @click="addBracket"
            :disabled="brackets.length >= 10"
            type="button"
            outlined
            size="small"
          />

          <!-- Bracket Validation Errors -->
          <div v-if="bracketValidationErrors.length > 0" class="bracket-validation-errors">
            <inline-message
              v-for="(error, idx) in bracketValidationErrors"
              :key="idx"
              severity="error"
              :closable="false"
            >
              {{ error }}
            </inline-message>
          </div>

          <!-- Preview Calculator -->
          <div class="commission-preview">
            <h5>{{ t('Prévia de Cálculo') }}</h5>
            <div class="preview-input">
              <label>{{ t('Receita Teste') }}</label>
              <input-currency
                elem-id="payroll_preview_revenue"
                data-testid="payroll-payment-rules-step4-preview-revenue"
                v-model="previewRevenue"
                :placeholder="t('R$ 0,00')"
              />
            </div>
            <div class="preview-result">
              <span>{{ t('Comissão Calculada:') }}</span>
              <strong class="amount">{{ n(calculatePreviewCommission() || 0, 'currency') }}</strong>
            </div>
            <div class="preview-explanation">
              <small v-if="wizardData.commission_structure === 'tiered'">
                {{ t('Escalonada: Toda receita recebe a taxa da faixa atingida') }}
              </small>
              <small v-else-if="wizardData.commission_structure === 'progressive'">
                {{ t('Progressiva: Soma das comissões de cada faixa') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Description (Optional) -->
      <div class="form-field">
        <label>{{ t('Descrição (opcional)') }}</label>
        <input-rich-text
          data-testid="payroll-payment-rules-step4-description-input"
          elem-id="description"
          v-model="wizardData.description"
          :label="t('Adicione observações sobre esta regra...')"
          :placeholder="t('Descreva o objetivo desta regra, condições especiais, etc.')"
          :show-toolbar="true"
          min-height="100px"
        />
      </div>
    </form>

    <!-- Comparison Modal -->
    <modal
      :show="showComparison"
      :title="t('Comparação Visual: Estruturas de Comissão')"
      size="large"
      elem-id="modal_payroll-commission-comparison"
      @close="showComparison = false"
      @dismiss="showComparison = false"
    >
      <div class="comparison-modal">
        <div class="comparison-slider">
          <label>{{ t('Ajuste Receita de Exemplo:') }}</label>
          <input-slider
            data-testid="payroll-payment-rules-step4-comparison-slider"
            v-model="comparisonRevenue"
            :min="0"
            :max="100000"
            :step="5000"
          />
          <div class="revenue-display">{{ n(comparisonRevenue, 'currency') }}</div>
        </div>

        <div class="comparison-results">
          <div v-for="structure in commissionStructures" :key="structure.id" class="comparison-card">
            <h4>{{ structure.name }}</h4>
            <div class="comparison-example">
              <small>{{ structure.shortExplanation }}</small>
            </div>
            <div class="comparison-result">
              <span>{{ t('Comissão:') }}</span>
              <strong>{{ n(calculateComparison(structure.id), 'currency') }}</strong>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, inject, ref } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useI18n as useI18nFormatter } from 'vue-i18n';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { log } from '@/utils/DebugLogger';

type Id = number | string;
type CommissionStructureType = 'simple' | 'tiered' | 'progressive';

interface Bracket {
  min: number;
  max: number | null;
  /** Rate stored as fraction (0..1). PRESERVED — basis-point semantics. */
  rate: number;
  /** Rate as integer/decimal percent (0..100). UI mirror only. */
  ratePercent: number;
}

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  commission_structure?: CommissionStructureType;
  /** Amount semantics: fixed = currency, commission = fraction (0..1). PRESERVED. */
  amount?: number;
  period_type?: string;
  description?: string;
  meta?: string | null;
  [key: string]: unknown;
}

interface WizardMachine {
  wizardData: Ref<WizardData>;
  next: () => void;
  cancel?: () => void;
}

interface CommissionStructureCard {
  id: CommissionStructureType;
  icon: string;
  name: string;
  description: string;
  shortExplanation: string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

// Inject machine from parent
const machine = inject<WizardMachine | null>('wizardMachine', null);

const { t } = useI18n();
const { n } = useI18nFormatter();
const { validateBrackets } = usePaymentRules();

// Use wizard data from machine
const wizardData = computed<WizardData>(() => machine?.wizardData.value || {});

const showComparison = ref<boolean>(false);
const comparisonRevenue = ref<number>(45000);
const previewRevenue = ref<number>(45000);

// Commission percentage converter (0-100 for display, 0-1 for storage)
// PRESERVED: stored amount stays as fraction; UI shows percent.
const commissionPercentage = computed<number>({
  get: () => {
    return Math.round((wizardData.value.amount || 0) * 100 * 100) / 100;
  },
  set: (value: number) => {
    wizardData.value.amount = value / 100;
  }
});

const commissionStructures = computed<CommissionStructureCard[]>(() => [
  {
    id: 'simple',
    icon: 'pi pi-percentage',
    name: t('Simples'),
    description: t('Uma única taxa sobre toda a receita'),
    shortExplanation: t('10% de R$50k = R$5k (toda receita)')
  },
  {
    id: 'tiered',
    icon: 'pi pi-chart-line',
    name: t('Escalonada'),
    description: t('Taxa muda quando atinge limite (aplica a toda receita)'),
    shortExplanation: t('R$50k > R$30k → 5% sobre TUDO = R$2,5k')
  },
  {
    id: 'progressive',
    icon: 'pi pi-sort-amount-up',
    name: t('Progressiva'),
    description: t('Como IR: taxas diferentes para faixas diferentes'),
    shortExplanation: t('R$30k@3% + R$20k@5% = R$900 + R$1k = R$1,9k')
  }
]);

const periodTypes = computed<SelectOption[]>(() => [
  { ID: 'weekly', Name: t('Semanal (7 dias)') },
  { ID: 'biweekly', Name: t('Quinzenal (14 dias)') },
  { ID: 'monthly', Name: t('Mensal (30 dias)') }
]);

// PRESERVED: default brackets (rate as fraction): 0..29999.99 @ 3%, 30000..∞ @ 5%
const brackets = ref<Bracket[]>([
  { min: 0, max: 29999.99, rate: 0.03, ratePercent: 3 },
  { min: 30000, max: null, rate: 0.05, ratePercent: 5 }
]);

const isCommissionRule = computed<boolean>(() => {
  return !!wizardData.value.rule_type && wizardData.value.rule_type.includes('commission');
});

const shouldShowAmountInput = computed<boolean>(() => {
  if (!isCommissionRule.value) return true; // Fixed rules always show amount
  return wizardData.value.commission_structure === 'simple';
});

const shouldShowBracketEditor = computed<boolean>(() => {
  return isCommissionRule.value && wizardData.value.commission_structure !== 'simple';
});

const isPeriodTypeValid = computed<boolean>(() => {
  if (wizardData.value.rule_type !== 'fixed') return true;
  return wizardData.value.period_type !== null && wizardData.value.period_type !== '' && wizardData.value.period_type !== undefined;
});

const isAmountValid = computed<boolean>(() => {
  const amount = wizardData.value.amount || 0;
  if (amount <= 0) return false;
  // PRESERVED: commission rate fraction must not exceed 1.0 (100%)
  if (isCommissionRule.value && amount > 1.0) return false;
  return true;
});

const bracketValidationErrors = computed<string[]>(() => {
  if (!shouldShowBracketEditor.value) return [];

  // PRESERVED: pass fraction rate to validator
  const bracketsForValidation = brackets.value.map(b => ({
    min: b.min,
    max: b.max,
    rate: b.rate
  }));

  return validateBrackets(bracketsForValidation, wizardData.value.commission_structure as CommissionStructureType) as string[];
});

const canProceed = computed<boolean>(() => {
  if (wizardData.value.rule_type === 'fixed') {
    return isPeriodTypeValid.value && isAmountValid.value;
  }

  if (isCommissionRule.value) {
    if (wizardData.value.commission_structure === 'simple') {
      return isAmountValid.value;
    }
    return bracketValidationErrors.value.length === 0;
  }

  return false;
});

const selectCommissionStructure = (structureId: CommissionStructureType): void => {
  wizardData.value.commission_structure = structureId;

  // Initialize default brackets if needed
  if (structureId !== 'simple' && brackets.value.length === 0) {
    brackets.value = [
      { min: 0, max: 29999.99, rate: 0.03, ratePercent: 3 },
      { min: 30000, max: null, rate: 0.05, ratePercent: 5 }
    ];
  }
};

const addBracket = (): void => {
  const lastBracket = brackets.value[brackets.value.length - 1];
  if (lastBracket.max === null) {
    lastBracket.max = (lastBracket.min || 0) + 10000;
  }

  brackets.value.push({
    min: (lastBracket.max as number) + 0.01,
    max: null,
    rate: 0.05,
    ratePercent: 5
  });
};

const removeBracket = (index: number): void => {
  brackets.value.splice(index, 1);
  if (brackets.value.length > 0) {
    brackets.value[brackets.value.length - 1].max = null;
  }
};

const calculatePreviewCommission = (): number => {
  const revenue = Number(previewRevenue.value) || 0;
  if (revenue <= 0) return 0;

  if (wizardData.value.commission_structure === 'simple' || !isCommissionRule.value) {
    return revenue * (wizardData.value.amount || 0);
  }

  if (wizardData.value.commission_structure === 'tiered') {
    // PRESERVED tiered semantics: matched bracket's rate applies to entire revenue
    for (const bracket of brackets.value) {
      if (revenue >= bracket.min && (bracket.max === null || revenue <= bracket.max)) {
        return revenue * bracket.rate;
      }
    }
    return 0;
  }

  if (wizardData.value.commission_structure === 'progressive') {
    // PRESERVED progressive (IR-style): each bracket's portion taxed at its own rate
    let total = 0;
    for (const bracket of brackets.value) {
      const min = bracket.min;
      const max = bracket.max ?? revenue;
      const amount = Math.min(revenue, max) - min;
      if (amount > 0) {
        total += amount * bracket.rate;
      }
      if (max >= revenue) break;
    }
    return total;
  }

  return 0;
};

const calculateComparison = (structureId: CommissionStructureType): number => {
  const revenue = Number(comparisonRevenue.value) || 0;

  // PRESERVED: comparison uses hard-coded 10% / 3-5% rates for visual comparison only.
  if (structureId === 'simple') {
    return revenue * 0.10; // Assume 10% for comparison
  }

  if (structureId === 'tiered') {
    // 3% up to 30k, 5% after
    if (revenue > 30000) {
      return revenue * 0.05;
    }
    return revenue * 0.03;
  }

  if (structureId === 'progressive') {
    // 3% on first 30k, 5% on remainder
    let total = 0;
    if (revenue > 30000) {
      total += 30000 * 0.03;
      total += (revenue - 30000) * 0.05;
    } else {
      total = revenue * 0.03;
    }
    return total;
  }

  return 0;
};

const handleNext = (): void => {
  // Update meta field based on selections
  if (wizardData.value.rule_type === 'fixed') {
    wizardData.value.meta = JSON.stringify({
      period_type: wizardData.value.period_type
    });
  } else if (wizardData.value.commission_structure !== 'simple') {
    const meta = {
      commission_structure: {
        type: wizardData.value.commission_structure,
        brackets: brackets.value.map(b => ({
          min: b.min,
          max: b.max,
          rate: b.rate
        }))
      }
    };
    wizardData.value.meta = JSON.stringify(meta);
    log('Step4Calculation', 'handleNext', 'Saving brackets to meta', {
      commission_structure: wizardData.value.commission_structure,
      bracketsCount: brackets.value.length,
      meta: wizardData.value.meta
    });
  }

  machine?.next();
};

const handleCancel = (): void => {
  machine?.cancel?.();
};

// Sync brackets rate with ratePercent — PRESERVED: rate = ratePercent/100 (fraction)
watch(brackets, (newBrackets) => {
  newBrackets.forEach(bracket => {
    bracket.rate = bracket.ratePercent / 100;
  });
}, { deep: true });

// Initialize brackets from meta if editing
onMounted(() => {
  if (wizardData.value.meta) {
    try {
      const meta = JSON.parse(wizardData.value.meta);
      if (meta.commission_structure?.brackets) {
        brackets.value = (meta.commission_structure.brackets as Array<{ min: number; max: number | null; rate: number }>).map(b => ({
          min: b.min,
          max: b.max,
          rate: b.rate,
          ratePercent: Math.round(b.rate * 100 * 100) / 100
        }));
      }
    } catch (e) {
      log('Step4Calculation', 'onMounted', 'Error parsing meta', e);
    }
  }
});
</script>

<style scoped>
.step-calculation {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.commission-types {
  margin: 0 0 0.75rem 0;
  padding-left: 1.25rem;
}

.commission-types li {
  margin-bottom: 0.5rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

.wizard-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.tooltip-icon {
  color: var(--primary-color);
  cursor: help;
  font-size: 0.875rem;
}

.field-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.structure-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.25rem;
}

.structure-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 1rem;
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.structure-option:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.structure-option:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.structure-option.selected {
  border-color: var(--primary-color);
  background: var(--primary-50);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.structure-option .card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  font-size: 1.5rem;
  color: var(--primary-color);
  background: var(--primary-50);
  border-radius: 8px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.structure-option.selected .card-icon {
  background: var(--primary-100);
  color: var(--primary-color);
}

.structure-option .card-content {
  flex: 1;
  min-width: 0;
}

.structure-option .card-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
}

.structure-option .card-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}

.structure-option .card-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.structure-option .card-indicator::before {
  content: '';
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.structure-option.selected .card-indicator::before {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.commission-slider-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.commission-value-display {
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 2px solid var(--primary-color);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.rate-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--orange-50);
  border-left: 3px solid var(--orange-500);
  border-radius: 4px;
  font-size: 0.875rem;
  color: var(--orange-900);
}

.rate-warning i {
  color: var(--orange-500);
}

.bracket-field {
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 6px;
  border: 1px solid var(--surface-border);
}

.bracket-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.bracket-row {
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--surface-0);
  border-radius: 6px;
  border: 1px solid var(--surface-border);
}

.bracket-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.field-group-actions {
  display: flex;
  align-items: center;
  padding-bottom: 0.25rem;
}

.commission-preview {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--surface-0);
  border: 2px solid var(--primary-color);
  border-radius: 6px;
}

.commission-preview h5 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
}

.preview-input {
  margin-bottom: 1rem;
}

.preview-input label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color);
}

.preview-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--surface-50);
  border-radius: 4px;
  font-size: 1rem;
}

.preview-result span {
  color: var(--text-color);
}

.preview-result .amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
}

.preview-explanation {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--surface-border);
}

.preview-explanation small {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  line-height: 1.4;
}

.bracket-validation-errors {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Comparison Modal */
.comparison-modal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.comparison-slider {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 6px;
}

.comparison-slider label {
  font-weight: 600;
  color: var(--text-color);
}

.revenue-display {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.comparison-results {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.comparison-card {
  padding: 1rem;
  background: var(--surface-0);
  border: 2px solid var(--surface-border);
  border-radius: 6px;
}

.comparison-card h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  color: var(--primary-color);
}

.comparison-example {
  margin-bottom: 0.75rem;
  min-height: 2.5rem;
}

.comparison-example small {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  line-height: 1.4;
}

.comparison-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: var(--surface-50);
  border-radius: 4px;
}

.comparison-result span {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.comparison-result strong {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-color);
}

.readonly-banner {
  margin: 1rem 0;
}

.readonly-banner :deep(.p-message-wrapper) {
  padding: 1rem;
}

.readonly-banner .banner-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.readonly-banner .banner-content strong {
  font-size: 1rem;
  color: #b7a026;
}

.readonly-banner .banner-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.readonly-banner .banner-actions {
  margin-top: 0.5rem;
}

.readonly-banner :deep(.pi-lock) {
  color: #b7a026;
}

@media (max-width: 768px) {
  .structure-options {
    grid-template-columns: 1fr;
  }

  .bracket-fields {
    grid-template-columns: 1fr;
  }

  .comparison-results {
    grid-template-columns: 1fr;
  }
}
</style>
